import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { ChevronDown, User, Briefcase, ArrowLeftRight } from 'lucide-react';

const RoleSwitcher = () => {
  const { user, activeRole, switchRole, language } = useContext(AuthContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  const roles = user.roles || [user.role];
  const hasMultipleRoles = roles.length > 1;
  const missingRole = roles.includes('client') && !roles.includes('professional')
    ? 'professional'
    : !roles.includes('client') && roles.includes('professional')
    ? 'client'
    : null;

  const copy = {
    en: { client: 'Client Mode', professional: 'Professional Mode', addClient: 'Add Client Profile', addProfessional: 'Add Professional Profile', switchTo: 'Switch to', active: 'Active', manage: 'Manage subscriptions & roles' },
    de: { client: 'Kundenmodus', professional: 'Profi-Modus', addClient: 'Kundenprofil hinzufügen', addProfessional: 'Profi-Profil hinzufügen', switchTo: 'Wechseln zu', active: 'Aktiv', manage: 'Abonnements & Rollen verwalten' },
    it: { client: 'Modalità cliente', professional: 'Modalità professionista', addClient: 'Aggiungi profilo cliente', addProfessional: 'Aggiungi profilo professionale', switchTo: 'Passa a', active: 'Attivo', manage: 'Gestisci abbonamenti e ruoli' },
    fr: { client: 'Mode client', professional: 'Mode professionnel', addClient: 'Ajouter un profil client', addProfessional: 'Ajouter un profil professionnel', switchTo: 'Passer à', active: 'Actif', manage: 'Gérer les abonnements et rôles' },
  }[language] || { client: 'Client Mode', professional: 'Professional Mode', addClient: 'Add Client Profile', addProfessional: 'Add Professional Profile', switchTo: 'Switch to', active: 'Active', manage: 'Manage subscriptions & roles' };
  const roleConfig = {
    client: { label: copy.client, icon: User, color: 'text-blue-600', bg: 'bg-blue-50', dash: '/dashboard/client' },
    professional: { label: copy.professional, icon: Briefcase, color: 'text-red-600', bg: 'bg-red-50', dash: '/dashboard/professional' },
  };

  const current = roleConfig[activeRole] || roleConfig[user.role];
  const CurrentIcon = current?.icon || User;

  if (!hasMultipleRoles) {
    if (!missingRole) return null;
    return (
      <button
        data-testid="add-role-btn"
        onClick={() => navigate(`/add-role/${missingRole}`)}
        className="flex h-9 w-9 items-center justify-center text-sm font-medium text-red-600 hover:text-red-700 border border-red-200 hover:border-red-400 rounded-full transition-all md:w-auto md:gap-2 md:px-3"
        aria-label={missingRole === 'professional' ? copy.addProfessional : copy.addClient}
        title={missingRole === 'professional' ? copy.addProfessional : copy.addClient}
      >
        <span className="text-xs">+</span>
        <span className="hidden md:inline">{missingRole === 'professional' ? copy.addProfessional : copy.addClient}</span>
      </button>
    );
  }

  return (
    <div className="relative" data-testid="role-switcher">
      <button
        data-testid="role-switcher-btn"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-sm font-medium border border-gray-200 rounded-full px-3 py-1.5 hover:bg-gray-50 transition-all"
      >
        <CurrentIcon className={`w-4 h-4 ${current?.color}`} />
        <span className="text-gray-700">{current?.label}</span>
        <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
            <div className="p-2">
              <p className="text-xs text-gray-400 px-2 py-1 font-medium uppercase tracking-wide">{copy.switchTo}</p>
              {roles.map((role) => {
                const cfg = roleConfig[role];
                if (!cfg) return null;
                const Icon = cfg.icon;
                const isActive = role === activeRole;
                return (
                  <button
                    key={role}
                    data-testid={`switch-to-${role}`}
                    onClick={() => {
                      switchRole(role);
                      navigate(cfg.dash);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      isActive ? `${cfg.bg} ${cfg.color} font-semibold` : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isActive ? cfg.color : 'text-gray-400'}`} />
                    {cfg.label}
                    {isActive && <span className="ml-auto text-xs font-normal opacity-60">{copy.active}</span>}
                  </button>
                );
              })}
              <div className="border-t border-gray-100 mt-2 pt-2">
                <button
                  data-testid="manage-roles-btn"
                  onClick={() => { navigate('/pricing'); setOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs text-gray-400 hover:bg-gray-50 transition-colors"
                >
                  <ArrowLeftRight className="w-3.5 h-3.5" />
                  {copy.manage}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RoleSwitcher;
